import React, { useEffect, useMemo, useState } from "react"
import { X, Save, Trash2, ArrowRight } from "lucide-react"

import { getToolById } from "../registry"
import Button from "../../../components/ui/button/button"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"

/**
 * Detail drawer for a selected connection (edge). Lets you describe what the
 * source step's result must be for the flow to follow this path - the same
 * routing the seed pipeline uses to pick a sub-agent (classification /
 * content_type), plus a freeform "custom" option for prototype flows.
 *
 *   condition.kind:
 *     always         -> unconditional
 *     classification -> source classifier verdict equals `value`
 *     content_type   -> rate-card content_types includes `value`
 *     custom         -> freeform expression in `value`
 *
 * Edits are applied to the graph (label updates immediately) and persisted by
 * the canvas "Save layout", matching the node drawer's "Apply to flow" flow.
 */

const KIND_OPTIONS = {
	always: "Always (unconditional)",
	classification: "Classification matches",
	content_type: "Content type includes",
	custom: "Custom condition",
}

// Presets for the supplier pipeline; freeform flows use the custom option.
const CLASSIFICATION_OPTIONS = {
	"supplier.rate_card": "supplier.rate_card",
	"supplier.contract": "supplier.contract",
	"supplier.discount_sales": "supplier.discount_sales",
	"supplier.stop_sales": "supplier.stop_sales",
	"supplier.information": "supplier.information",
}

const CONTENT_TYPE_OPTIONS = {
	accommodation: "accommodation",
	green_fee: "green_fee",
	package: "package",
}

// Top-level output field names the source node produces, so the user knows what
// they can branch on. Uses the seed/default schema (synchronous) as a hint.
const sourceOutputFields = (sourceNode) => {
	if (sourceNode?.type !== "agent") return []
	const schema =
		sourceNode.data?.binding === "tool"
			? getToolById(sourceNode.data.toolId)?.defaults?.json_schema
			: sourceNode.data?.draft?.json_schema
	const props = schema?.properties || {}
	return Object.keys(props)
}

function EdgeDrawer({ edge, sourceNode, targetNode, onClose, onUpdateEdge, onDeleteEdge }) {
	const [kind, setKind] = useState("always")
	const [value, setValue] = useState("")
	const [note, setNote] = useState("")
	const [savedNote, setSavedNote] = useState(null)

	useEffect(() => {
		const condition = edge?.data?.condition || { kind: "always" }
		setKind(condition.kind || "always")
		setValue(condition.value || "")
		setNote(edge?.data?.note || "")
		setSavedNote(null)
		// Re-init only when switching to a different edge.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edge?.id])

	const outputs = useMemo(() => sourceOutputFields(sourceNode), [sourceNode])

	if (!edge) return null

	const sourceLabel = sourceNode?.data?.label || edge.source
	const targetLabel = targetNode?.data?.label || edge.target

	const apply = () => {
		const condition =
			kind === "always" ? { kind: "always" } : { kind, value: value.trim() }
		onUpdateEdge?.(edge.id, { condition, note: note.trim() })
		setSavedNote("Applied to flow - use Save layout to persist")
	}

	return (
		<aside className="ai-flow-drawer" role="dialog" aria-label="Connection details">
			<header className="ai-flow-drawer-header">
				<div className="ai-flow-drawer-header-text">
					<h3>Connection</h3>
				</div>
				<button type="button" className="ai-flow-drawer-close" onClick={onClose} aria-label="Close">
					<X size={18} />
				</button>
			</header>

			<div className="ai-flow-drawer-body">
				{savedNote && <p className="ai-flow-drawer-saved-note">{savedNote}</p>}

				<div className="ai-flow-drawer-route">
					<span className="ai-flow-drawer-route-node">{sourceLabel}</span>
					<ArrowRight size={14} />
					<span className="ai-flow-drawer-route-node">{targetLabel}</span>
				</div>

				<p className="ai-flow-drawer-hint">
					Set what {sourceLabel}'s result must be for the flow to follow this path to{" "}
					{targetLabel}.
				</p>

				{outputs.length > 0 && (
					<div className="ai-flow-drawer-section">
						<span className="ai-flow-drawer-section-label">
							{sourceLabel} outputs
							<small> branch on one of these</small>
						</span>
						<div className="ai-flow-drawer-output-chips">
							{outputs.map((field) => (
								<code key={field}>{field}</code>
							))}
						</div>
					</div>
				)}

				<div className="ai-flow-drawer-section">
					<span className="ai-flow-drawer-section-label">Condition</span>
					<Select
						showLabel
						placeholder="When:"
						value={kind}
						selected={kind}
						onSelect={(option) => setKind(option.option)}
						options={KIND_OPTIONS}
					/>
				</div>

				{kind === "classification" && (
					<div className="ai-flow-drawer-section">
						<span className="ai-flow-drawer-section-label">Classification value</span>
						<Select
							showLabel
							placeholder="Classification:"
							value={value}
							selected={value}
							onSelect={(option) => setValue(option.option)}
							options={CLASSIFICATION_OPTIONS}
						/>
					</div>
				)}

				{kind === "content_type" && (
					<div className="ai-flow-drawer-section">
						<span className="ai-flow-drawer-section-label">Content type</span>
						<Select
							showLabel
							placeholder="Content type:"
							value={value}
							selected={value}
							onSelect={(option) => setValue(option.option)}
							options={CONTENT_TYPE_OPTIONS}
						/>
					</div>
				)}

				{kind === "custom" && (
					<div className="ai-flow-drawer-section">
						<span className="ai-flow-drawer-section-label">Custom condition</span>
						<Input
							type="text"
							placeholder="e.g. confidence_score > 0.8"
							value={value}
							onChange={setValue}
							showLabel
						/>
					</div>
				)}

				<div className="ai-flow-drawer-section">
					<span className="ai-flow-drawer-section-label">Note (optional)</span>
					<Textarea
						placeholder="Freeform note about this connection"
						value={note}
						onChange={setNote}
						showLabel
						rows={3}
					/>
				</div>
			</div>

			<footer className="ai-flow-drawer-footer">
				<span className="ai-flow-drawer-delete">
					<Button
						type="OUTLINE"
						small={true}
						label="Delete connection"
						icon={<Trash2 size={13} />}
						onClick={() => onDeleteEdge?.(edge.id)}
					/>
				</span>
				<div className="ai-flow-drawer-footer-actions">
					<Button label="Apply to flow" icon={<Save size={14} />} onClick={apply} />
				</div>
			</footer>
		</aside>
	)
}

export default EdgeDrawer
